import React, { useCallback, useEffect, useState } from "react"

import {
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@coval/ui"

import type { TraceTurnRangeFilterConfig } from "@/types/metrics/metadata"

import { TRACE_TURN_RANGE_MODES } from "../types"

interface TurnRangeFilterEditorProps {
  filter: TraceTurnRangeFilterConfig
  onChange: (filter: TraceTurnRangeFilterConfig) => void
}

type TurnRangeMode = TraceTurnRangeFilterConfig["mode"]

export const TurnRangeFilterEditor: React.FC<TurnRangeFilterEditorProps> = ({
  filter,
  onChange,
}) => {
  const [localCount, setLocalCount] = useState(filter.count.toString())
  const [localSpanName, setLocalSpanName] = useState(filter.span_name ?? "")

  useEffect(() => {
    setLocalCount(filter.count.toString())
  }, [filter.count])

  useEffect(() => {
    setLocalSpanName(filter.span_name ?? "")
  }, [filter.span_name])

  const handleModeChange = useCallback(
    (mode: string) => {
      onChange({ ...filter, mode: mode as TurnRangeMode })
    },
    [filter, onChange],
  )

  const handleCountChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value
    setLocalCount(value)
    const parsed = Number(value)
    if (Number.isInteger(parsed) && parsed >= 1) {
      onChange({ ...filter, count: parsed })
    }
  }

  const handleCountBlur = () => {
    const parsed = Number(localCount)
    const normalized = Number.isInteger(parsed) && parsed >= 1 ? parsed : 1
    setLocalCount(normalized.toString())
    if (filter.count !== normalized) {
      onChange({ ...filter, count: normalized })
    }
  }

  const handleSpanNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const next = event.target.value
    setLocalSpanName(next)
    if (next === "") {
      // Drop the key entirely so backend treats it as "any span".
      const { span_name: _ignored, ...rest } = filter
      onChange(rest as TraceTurnRangeFilterConfig)
    } else {
      onChange({ ...filter, span_name: next })
    }
  }

  return (
    <div className="flex items-center gap-2">
      <Select value={filter.mode} onValueChange={handleModeChange}>
        <SelectTrigger className="w-24" aria-label="Trace turn range mode">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {TRACE_TURN_RANGE_MODES.map((mode) => (
            <SelectItem key={mode.value} value={mode.value}>
              {mode.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Input
        type="number"
        min={1}
        step={1}
        value={localCount}
        onChange={handleCountChange}
        onBlur={handleCountBlur}
        aria-label="Turn count"
        className="w-20"
      />

      <span className="text-sm text-coval-text-muted">spans of</span>

      <Input
        type="text"
        value={localSpanName}
        onChange={handleSpanNameChange}
        placeholder="any span"
        aria-label="Trace turn range span name"
        className="flex-1"
      />
    </div>
  )
}
